import { type MouseEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import { MAIN_SITE_URL } from '@/mocks/site';
import { getArticle, articles, type ArticleLocalized } from '@/mocks/articles';

export default function Article() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();

  const article = slug ? getArticle(slug) : undefined;
  const lang = (i18n.language || 'en').split('-')[0];

  const localized = (value: ArticleLocalized) => value[lang as 'en' | 'pl' | 'es' | 'fr'] || value.en;

  if (!article) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Article not found</h1>
          <Link to="/" className="mt-4 inline-block text-sm font-medium text-primary-600">
            {t('common.backHome')}
          </Link>
        </div>
      </Layout>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 4);

  const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://usfanssheet.beer').replace(/\/$/, '');

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: localized(article.title),
    description: localized(article.desc),
    author: { '@type': 'Organization', name: 'USFans Spreadsheet' },
    publisher: { '@type': 'Organization', name: 'USFans Spreadsheet' },
    datePublished: '2026-08-20',
    dateModified: '2026-08-20',
    mainEntityOfPage: `${siteUrl}/article/${article.slug}`,
  };

  const handleBodyClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const anchor = target.closest('a');
    if (anchor && anchor.getAttribute('href') === '/') {
      e.preventDefault();
      navigate('/');
    }
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-foreground-900">
            {t('nav.home')}
          </Link>
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm"></i>
          </span>
          <span className="text-foreground-700">{t('nav.guides')}</span>
          <span className="flex h-3 w-3 items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm"></i>
          </span>
          <span className="truncate text-foreground-900">{localized(article.title)}</span>
        </nav>

        <header className="mt-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
            {article.category}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {localized(article.title)}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">{localized(article.desc)}</p>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500">
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-user-line text-sm"></i>
              </span>
              {t('article.author')}
            </span>
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-time-line text-sm"></i>
              </span>
              {article.readingTime}
            </span>
            <span className="flex items-center gap-2">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-sm"></i>
              </span>
              {t('article.updated')}
            </span>
          </div>
        </header>

        <div
          className="article-content mt-8 text-[15px]"
          onClick={handleBodyClick}
          dangerouslySetInnerHTML={{ __html: article.body }}
        />

        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="font-heading text-lg font-bold text-foreground-950">{t('home.articlesTitle')}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/article/${a.slug}`}
                className="rounded-full bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:text-primary-600"
              >
                {localized(a.title)}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-arrow-right-up-line text-lg"></i>
            </span>
            {t('common.explore')}
          </a>
          <Link
            to="/"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-foreground-300 px-7 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-arrow-left-line text-lg"></i>
            </span>
            {t('common.backHome')}
          </Link>
        </div>
      </article>
    </Layout>
  );
}